const APPROVED_STATUSES = new Set(['approved', 'verified', 'success']);
const FAILED_STATUSES = new Set([
  'failed',
  'declined',
  'rejected',
  'expired',
  'cancelled',
]);
const PENDING_STATUSES = new Set([
  'pending',
  'in_progress',
  'awaiting_user',
  'resubmission_required',
]);

export function normaliseIdentityStatus(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

export function classifyIdentityStatus(identityStatus) {
  const status = normaliseIdentityStatus(identityStatus?.status);

  if (
    identityStatus?.identity_verified ||
    identityStatus?.adult_verified ||
    APPROVED_STATUSES.has(status)
  ) {
    return 'approved';
  }

  if (FAILED_STATUSES.has(status)) {
    return 'failed';
  }

  if (PENDING_STATUSES.has(status)) {
    return 'pending';
  }

  if (!status || status === 'not_started') {
    return 'available';
  }

  return 'unlinked';
}

export function deriveIdentityPhase({
  dobStatus,
  identityStatus,
  loading = false,
  error = '',
}) {
  if (loading) return 'loading';
  if (!dobStatus) return error ? 'unlinked' : 'loading';

  if (!dobStatus.date_of_birth_set) {
    return 'missing_dob';
  }

  if (
    dobStatus.guardian_required ||
    dobStatus.age_band === '15_to_17' ||
    dobStatus.didit_verification_blocked_reason === 'guardian_verification_required'
  ) {
    return 'guardian_required';
  }

  if (
    dobStatus.age_band === 'under_15' ||
    dobStatus.age_band === 'unknown' ||
    dobStatus.didit_verification_blocked_reason === 'ineligible_age' ||
    dobStatus.didit_verification_blocked_reason === 'age_not_eligible'
  ) {
    return 'blocked';
  }

  const identityPhase = classifyIdentityStatus(identityStatus);

  if (identityPhase === 'approved') return 'approved';
  if (identityPhase === 'failed') return 'failed';
  if (identityPhase === 'pending') return 'pending';

  if (dobStatus.identity_verification_eligible) {
    return identityPhase === 'unlinked' ? 'unlinked' : 'available';
  }

  return 'blocked';
}

export function isIdentityAccountBlocked(user) {
  if (!user) return true;
  if (user.is_active === false) return true;

  const accountStatus = String(user.account_status || '').toLowerCase();
  if (['suspended', 'deleted', 'banned', 'disabled', 'closed'].includes(accountStatus)) {
    return true;
  }

  return user?.suspension?.status === 'active';
}

export const IDENTITY_PHASES = Object.freeze({
  LOADING: 'loading',
  MISSING_DOB: 'missing_dob',
  GUARDIAN_REQUIRED: 'guardian_required',
  BLOCKED: 'blocked',
  AVAILABLE: 'available',
  PENDING: 'pending',
  FAILED: 'failed',
  APPROVED: 'approved',
  UNLINKED: 'unlinked',
});
